import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, ClipboardList, CheckCircle,
    Clock, AlertCircle, ChevronRight
} from 'lucide-react';
import api from '../../api';
import DashboardLayout from '../../layouts/DashboardLayout';

const STATUS_BADGE = {
    completed: { label: 'Evaluated', cls: 'bg-green-100 text-green-700' },
    processing: { label: 'Processing', cls: 'bg-amber-100 text-amber-700' },
    pending: { label: 'Pending', cls: 'bg-secondary-100 text-secondary-500' },
    failed: { label: 'Failed', cls: 'bg-red-100 text-red-600' },
};

const ReporteeSessions = () => {
    const { reporteeId } = useParams();
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.getReporteeSessions(reporteeId)
            .then((res) => setSessions(res.data))
            .catch(() => setError('Failed to load sessions. Please try again.'))
            .finally(() => setLoading(false));
    }, [reporteeId]);

    if (loading) {
        return (
            <DashboardLayout>
                <div className="p-8 text-center text-secondary-500 animate-pulse">Loading sessions...</div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout>
                <div className="p-8 flex items-center gap-3 text-red-600">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="max-w-5xl mx-auto px-4 py-8">
                {/* Back + Header */}
                <button
                    onClick={() => navigate('/supervisor/reportees')}
                    className="flex items-center gap-1.5 text-sm text-secondary-500 hover:text-primary-600 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to My Team
                </button>

                <div className="mb-6">
                    <h1 className="text-2xl font-heading font-bold text-secondary-900 flex items-center gap-2">
                        <ClipboardList className="w-6 h-6 text-primary-600" />
                        Session History
                    </h1>
                    <p className="text-secondary-500 mt-1 text-sm">
                        All sessions for this clinician — click a row to view the LLM evaluation and benchmark.
                    </p>
                </div>

                {sessions.length === 0 ? (
                    <div className="bg-white rounded-xl border border-secondary-100 shadow-sm p-12 text-center">
                        <ClipboardList className="w-10 h-10 text-secondary-300 mx-auto mb-3" />
                        <p className="text-secondary-500">No sessions recorded yet.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-secondary-100 shadow-sm overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-secondary-100 bg-secondary-50/60">
                                    <th className="text-left px-5 py-3 text-xs font-bold text-secondary-400 uppercase tracking-wide">Session</th>
                                    <th className="text-left px-5 py-3 text-xs font-bold text-secondary-400 uppercase tracking-wide">Patient</th>
                                    <th className="text-left px-5 py-3 text-xs font-bold text-secondary-400 uppercase tracking-wide">Date</th>
                                    <th className="text-left px-5 py-3 text-xs font-bold text-secondary-400 uppercase tracking-wide">Framework</th>
                                    <th className="text-left px-5 py-3 text-xs font-bold text-secondary-400 uppercase tracking-wide">Status</th>
                                    <th className="px-5 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary-50">
                                {sessions.map((s) => {
                                    const badge = STATUS_BADGE[s.summary_generation_status] || STATUS_BADGE.pending;
                                    const canView = s.summary_generation_status === 'completed';
                                    return (
                                        <tr
                                            key={s.id}
                                            onClick={() => canView && navigate(`/dashboard/benchmark/${s.id}`)}
                                            className={`transition-colors ${canView ? 'hover:bg-primary-50 cursor-pointer' : 'opacity-60'}`}
                                        >
                                            <td className="px-5 py-4 font-mono text-secondary-500">#{s.id}</td>
                                            <td className="px-5 py-4 font-medium text-secondary-800">
                                                {s.patient?.name || '—'}
                                            </td>
                                            <td className="px-5 py-4 text-secondary-600">
                                                {s.sessiondate
                                                    ? new Date(s.sessiondate).toLocaleDateString('en-GB')
                                                    : s.created_at?.split(' ')[0]}
                                            </td>
                                            <td className="px-5 py-4 text-secondary-600 capitalize">
                                                {s.therapy_type || '—'}
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${badge.cls}`}>
                                                    {s.summary_generation_status === 'completed'
                                                        ? <CheckCircle className="w-3 h-3" />
                                                        : s.summary_generation_status === 'processing'
                                                            ? <Clock className="w-3 h-3" />
                                                            : <AlertCircle className="w-3 h-3" />}
                                                    {badge.label}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4">
                                                {canView && (
                                                    <div className="flex gap-2 justify-end">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/consultation/${s.id}`);
                                                            }}
                                                            className="flex items-center gap-1 text-xs text-secondary-500 hover:text-secondary-800 font-medium"
                                                        >
                                                            <ChevronRight className="w-3.5 h-3.5" />
                                                            Full Report
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default ReporteeSessions;
